import React from 'react';
import { Link } from 'react-router-dom';
import { Badge, Button, Table, phone } from '@parsina/framework';
import { Ban, Check, ChevronLeft } from 'lucide-react';

import { useTranslation, LTR_TEXT, MIRROR_ICON } from '../../i18n';
import { SubscriptionBadge, TierBadge } from '../../components/SubscriptionBadge';
import LapseHint from '../../components/LapseHint';
import {
  currencyName, isForeignCurrency, lapseAnchor, validUntil,
} from '../../lib/format';

/**
 * The estate as a table.
 *
 * Column set answers the question the screen exists for — «can this clinic work,
 * and until when» — which is why `دسترسی` (entitlement) is a column at all. It is
 * the single most decisive bit the BFF returns and no screen was showing it: an
 * operator could read ACTIVE and طلایی on a clinic that was, in fact, blocked.
 *
 * ROWS ARE OPERABLE BY KEYBOARD, twice over:
 *   • the whole row activates on click, Enter and Space (`onRowActivate`), which
 *     the kit added for this — a bare `<tr onClick>` made the entire list
 *     unreachable without a mouse;
 *   • the last cell holds a real `<a href>`, which is what makes middle-click,
 *     ⌘-click and "copy link" work. An operator keeping the estate in one tab and
 *     a clinic in another cannot do that with a click handler.
 *
 * Both are kept on purpose: the row is the fast path, the link is the honest one.
 */
export default function ClinicsTable({ rows, view, onSort, onOpen, empty }) {
  const { t, formatDate, locale } = useTranslation();

  const columns = [
    {
      key: 'name',
      header: t('clinics.columns.name'),
      sortable: true,
      render: (r) => (
        <div className="min-w-0">
          {/* `dir="auto"` — a clinic's name is DATA, not UI: it renders in the
              language it was entered in, whichever language the panel is in. */}
          <div dir="auto" className="truncate font-semibold text-foreground" title={r.name || ''}>
            {r.name || t('common.value.none')}
          </div>
          <div dir="auto" className="truncate text-xs text-muted-foreground">
            {r.clinicType || t('common.value.none')}
            {/* Only the exception is marked: nearly every clinic keeps its books
                in toman, and a code on every row would be noise. */}
            {isForeignCurrency(r.currency) ? (
              <Badge
                variant="outline"
                className="ms-1.5"
                title={t('clinics.currencyHint', { name: currencyName(r.currency, { t, locale }) || r.currency })}
                data-testid={`clinics-row-${r.id}-currency`}
              >
                <span {...LTR_TEXT}>{r.currency}</span>
              </Badge>
            ) : null}
          </div>
        </div>
      ),
    },
    {
      key: 'status',
      header: t('clinics.columns.status'),
      sortable: true,
      width: '11rem',
      render: (r) => (
        <div className="flex flex-wrap items-center gap-1">
          <SubscriptionBadge clinic={r} />
          {/* The registry's own flag, which is a different fact from the
              subscription's state and was not shown anywhere. */}
          {r.active === false ? (
            <Badge variant="outline" title={t('clinics.inactiveHint')}>
              {t('clinics.inactive')}
            </Badge>
          ) : null}
        </div>
      ),
    },
    {
      key: 'tier',
      header: t('clinics.columns.tier'),
      sortable: true,
      width: '7rem',
      render: (r) => (r.hasSubscription ? <TierBadge clinic={r} /> : t('common.value.none')),
    },
    {
      key: 'entitled',
      header: t('clinics.columns.entitled'),
      sortable: true,
      width: '7rem',
      // Icon AND word, never colour alone: this is the column an operator acts
      // on, and a green/red dot is unreadable in print, in monochrome, and to a
      // colour-blind reader.
      render: (r) => (
        <span
          className={`inline-flex items-center gap-1 text-xs font-semibold ${
            r.entitled ? 'text-[#166534]' : 'text-destructive'
          }`}
        >
          {r.entitled
            ? <Check size={13} aria-hidden />
            : <Ban size={13} aria-hidden />}
          {r.entitled ? t('clinics.entitled.yes') : t('clinics.entitled.no')}
        </span>
      ),
    },
    {
      key: 'until',
      header: t('clinics.columns.until'),
      sortable: true,
      width: '11rem',
      render: (r) => (
        <div className="flex flex-col">
          <span>{r.hasSubscription ? validUntil(r, { t, formatDate }) : t('common.value.none')}</span>
          <LapseHint date={lapseAnchor(r)} />
        </div>
      ),
    },
    {
      key: 'phone',
      header: t('clinics.columns.phone'),
      width: '8rem',
      // An LTR island: a phone number is a Latin-digit run and must read
      // left-to-right inside a Persian row, with `unicode-bidi: isolate` stopping
      // the neighbouring text from dragging its ends around.
      //
      // Shown in the NATIONAL form. The column is stored E.164 now
      // («+982112345678») and an operator scanning this table reads and dials
      // «02112345678». `phone.forDisplay` is the kit's read path: it tolerates a
      // landline — an office number IS one, and the strict converter answers ''
      // for every area code — and falls back to the raw value rather than
      // blanking a number it cannot parse.
      render: (r) => (r.officePhone
        ? <span {...LTR_TEXT} className="tabular-nums">{phone.forDisplay(r.officePhone)}</span>
        : t('common.value.none')),
    },
    {
      key: 'created',
      header: t('clinics.columns.created'),
      sortable: true,
      width: '8rem',
      render: (r) => (r.createdAt ? formatDate(r.createdAt) : t('common.value.none')),
    },
    {
      key: 'actions',
      // Visually hidden: the column is obvious from its contents, and a header
      // over a row of links is noise — but a `<th>` with no accessible text
      // leaves the column unnamed for a screen reader.
      header: <span className="sr-only">{t('clinics.columns.actions')}</span>,
      align: 'end',
      width: '6rem',
      render: (r) => (
        <Button
          asChild
          variant="ghost"
          size="sm"
          // The row's own handler would fire too and navigate twice.
          onClick={(e) => e.stopPropagation()}
          data-testid={`clinics-row-${r.id}-open`}
        >
          <Link to={`/clinics/${r.id}`} aria-label={t('clinics.open', { name: r.name || r.id })}>
            {t('common.actions.details')}
            {/* Directional glyph: "drill in" points toward the reading
                direction, so it mirrors under `ltr`. */}
            <ChevronLeft size={14} aria-hidden style={MIRROR_ICON} />
          </Link>
        </Button>
      ),
    },
  ];

  return (
    <Table
      dense
      ariaLabel={t('clinics.table.aria')}
      columns={columns}
      rows={rows}
      rowKey={(r) => r.id}
      onRowActivate={(r) => onOpen(r)}
      rowProps={(r) => ({ 'data-testid': `clinics-row-${r.id}` })}
      sort={{ key: view.sort, dir: view.dir }}
      onSort={onSort}
      empty={empty}
      data-testid="clinics-table"
    />
  );
}
